/** Policies (DESIGN.md, *Policies*): rules a formation follows on its own,
 *  as the server reads them (YAML, strict). The editor's model, YAML both
 *  ways (a block YAML writer, and a reader for the server's normalised
 *  output), plain-words text, and the presets. */

export type Leaf =
  | "always"
  | { enemies_within: { radius: number; at_least: number } }
  | { friends_within: { radius: number; at_least: number } }
  | { ammo_below: { pct: number } }
  | { strength_below: { pct: number } }
  | { health_below: { pct: number } }
  | { under_fire: { pct: number } }
  | { arrived: { pct: number } }
  | { stock_below: { good: number; amount: number } };
export type Cond = Leaf | { all: Cond[] } | { any: Cond[] } | { not: Cond };
export type Fire = "AtWill" | "Return" | "Hold";
export type Moves = "Maneuver" | "Hold";
export type Target = "nearest" | "armour" | "soft" | "structures";
export type Action =
  | "halt"
  | "withdraw"
  | "resume"
  | { posture: { fire: Fire; moves: Moves } }
  | { engage: { radius: number } }
  | { prefer: { target: Target } }
  | { produce: { design: number } }
  | { limit: { keep_steel: number; max_units: number } };
export interface Rule {
  priority: number;
  when: Cond;
  then: Action[];
}
export interface Policy {
  name: string;
  every: number;
  rules: Rule[];
}

/** A policy attached to one of the player's formations (state.policies). */
export interface Attached {
  formation: number;
  name: string;
  yaml: string;
  cost: number;
  last_rule: number | null;
  /** Its units' keys, where the server says (so a reload knows them). */
  units?: (string | number)[];
}

/** The server's word on a policy being edited (bake_policy). */
export interface Baked {
  ok: boolean;
  cost?: number;
  budget: number;
  every_needs?: number;
  yaml?: string;
  error?: string;
}

export const MAX_RADIUS = 48;
/** Ticks a second (state/history: 600 a minute). */
export const TICKS_PER_SECOND = 10;

export const LEAF_KINDS = ["always", "enemies_within", "friends_within", "ammo_below", "strength_below", "health_below", "under_fire", "arrived", "stock_below"] as const;
export type LeafKind = (typeof LEAF_KINDS)[number];
export const ACTION_KINDS = ["halt", "withdraw", "resume", "posture", "engage", "prefer", "produce", "limit"] as const;
/** What a formation's policy can do, and a barracks' (v31.7: a barracks
 *  reads its conditions round itself, and only what it makes or keeps). */
export const UNIT_ACTIONS = ["halt", "withdraw", "resume", "posture", "engage", "prefer"] as const;
export const BARRACKS_ACTIONS = ["produce", "limit"] as const;
export const BARRACKS_LEAVES = ["always", "enemies_within", "friends_within", "stock_below"] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];

export const LEAF_WORDS: Record<LeafKind, [label: string, tip: string]> = {
  always: ["Always", "Holds every check: the rule to fall back on when nothing above it holds"],
  enemies_within: ["Enemies within", "At least this many armed enemy units within this many hexes of the formation's centre"],
  friends_within: ["Friends within", "At least this many of your own armed units (the formation's own count) within this many hexes"],
  ammo_below: ["Ammo below", "The formation's ammunition, as a share of full, is below this"],
  strength_below: ["Strength below", "Fewer of its units left than this share of those it had when the policy was attached"],
  health_below: ["Health below", "The formation's hit points, as a share of full, are below this"],
  under_fire: ["Under fire", "At least this share of the formation's units are suppressed"],
  stock_below: ["Station has less than", "Its station holds less than this much of the good (a barracks' station: steel to pay, recruits, and its design's goods)"],
  arrived: ["Arrived", "At least this share of the formation's units are where their order sends them (a halted formation counts as arrived)"],
};
export const ACTION_WORDS: Record<ActionKind, [label: string, tip: string]> = {
  halt: ["Halt", "Stop where they are"],
  withdraw: ["Withdraw", "Fall back to your nearest depot"],
  resume: ["Resume orders", "Back to the orders the formation had when the policy was attached"],
  posture: ["Posture", "Set fire and move states, as Fire state and Move state do"],
  engage: ["Engage within", "Go after enemy strength within this many hexes"],
  prefer: ["Target first", "What they shoot at first when they have a choice: the nearest, armour, soft targets or structures"],
  produce: ["Make", "The barracks makes this design"],
  limit: ["Limits", "The barracks keeps this much steel and makes up to this many of its design (0: none)"],
};

/** Steel's good id (goods.rs), the stock condition's default. */
export const STEEL = 5;
/** Goods' names by id, for the stock condition's words and picker. */
let policyGoods: string[] = [];
export function setPolicyGoods(names: string[]): void {
  policyGoods = names;
}
export const goodsForPolicy = () => policyGoods;

/** Design names for the words (a barracks' designs, from the editor). */
let designNames = new Map<number, string>();
export function setDesignNames(list: { id: number; name: string }[]): void {
  designNames = new Map(list.map((d) => [d.id, d.name]));
}
export const TARGET_WORDS: [Target, string][] = [
  ["nearest", "Nearest"],
  ["armour", "Armour"],
  ["soft", "Soft"],
  ["structures", "Structures"],
];
export const FIRE_WORDS: [Fire, string][] = [
  ["AtWill", "Fire at will"],
  ["Return", "Return fire"],
  ["Hold", "Hold fire"],
];
export const MOVE_WORDS: [Moves, string][] = [
  ["Maneuver", "Maneuver"],
  ["Hold", "Hold position"],
];

export const leafKind = (c: Leaf): LeafKind => (typeof c === "string" ? c : (Object.keys(c)[0] as LeafKind));
export const actionKind = (a: Action): ActionKind => (typeof a === "string" ? a : (Object.keys(a)[0] as ActionKind));
const isLeaf = (c: Cond): c is Leaf => typeof c === "string" || LEAF_KINDS.includes(Object.keys(c)[0] as LeafKind);

export function newLeaf(k: LeafKind): Leaf {
  switch (k) {
    case "always":
      return "always";
    case "enemies_within":
      return { enemies_within: { radius: 20, at_least: 1 } };
    case "friends_within":
      return { friends_within: { radius: 10, at_least: 5 } };
    case "ammo_below":
      return { ammo_below: { pct: 25 } };
    case "strength_below":
      return { strength_below: { pct: 50 } };
    case "health_below":
      return { health_below: { pct: 50 } };
    case "under_fire":
      return { under_fire: { pct: 30 } };
    case "arrived":
      return { arrived: { pct: 80 } };
    case "stock_below":
      return { stock_below: { good: STEEL, amount: 2000 } };
  }
}
export function newAction(k: ActionKind): Action {
  if (k === "posture") return { posture: { fire: "Return", moves: "Hold" } };
  if (k === "engage") return { engage: { radius: 20 } };
  if (k === "prefer") return { prefer: { target: "armour" } };
  if (k === "produce") return { produce: { design: [...designNames.keys()][0] ?? 0 } };
  if (k === "limit") return { limit: { keep_steel: 1000, max_units: 0 } };
  return k;
}

/** A condition as the builder's rows: all or any of clauses, each perhaps
 *  negated; null if it's nested deeper than rows can show. */
export interface Clauses {
  join: "all" | "any";
  rows: { not: boolean; leaf: Leaf }[];
}
export function clausesOf(c: Cond): Clauses | null {
  const row = (x: Cond) => (isLeaf(x) ? { not: false, leaf: x } : "not" in x && isLeaf(x.not) ? { not: true, leaf: x.not } : null);
  const one = row(c);
  if (one) return { join: "all", rows: [one] };
  const join = typeof c === "string" ? null : "all" in c ? "all" : "any" in c ? "any" : null;
  if (!join) return null;
  const rows = (c as Record<string, Cond[]>)[join].map(row);
  return rows.every((r) => r) ? { join, rows: rows as Clauses["rows"] } : null;
}
export function condOf(k: Clauses): Cond {
  const one = (r: Clauses["rows"][number]): Cond => (r.not ? { not: r.leaf } : r.leaf);
  if (!k.rows.length) return "always";
  if (k.rows.length === 1) return one(k.rows[0]);
  return k.join === "all" ? { all: k.rows.map(one) } : { any: k.rows.map(one) };
}

// ---- Words ----

export function leafText(c: Leaf): string {
  if (typeof c === "string") return "always";
  if ("enemies_within" in c) return `${c.enemies_within.at_least}+ enemies within ${c.enemies_within.radius}`;
  if ("friends_within" in c) return `${c.friends_within.at_least}+ friends within ${c.friends_within.radius}`;
  if ("ammo_below" in c) return `ammo below ${c.ammo_below.pct}%`;
  if ("health_below" in c) return `health below ${c.health_below.pct}%`;
  if ("under_fire" in c) return `${c.under_fire.pct}%+ under fire`;
  if ("arrived" in c) return `${c.arrived.pct}%+ arrived`;
  if ("stock_below" in c) return `station has less than ${c.stock_below.amount.toLocaleString("en-US")} ${(policyGoods[c.stock_below.good] ?? `good ${c.stock_below.good}`).toLowerCase()}`;
  return `strength below ${c.strength_below.pct}%`;
}
export function condText(c: Cond): string {
  if (isLeaf(c)) return leafText(c);
  if ("not" in c) return `not ${condText(c.not)}`;
  // A group inside a group: in brackets, so "and" and "or" read right.
  const inner = (x: Cond) => (typeof x !== "string" && ("all" in x || "any" in x) ? `(${condText(x)})` : condText(x));
  if ("all" in c) return c.all.map(inner).join(" and ");
  return c.any.map(inner).join(" or ");
}
export function actionText(a: Action): string {
  if (typeof a === "string") return a === "resume" ? "resume orders" : a;
  if ("engage" in a) return `engage within ${a.engage.radius}`;
  if ("produce" in a) return `make ${(designNames.get(a.produce.design) ?? `design ${a.produce.design}`).toLowerCase()}`;
  if ("limit" in a) return [a.limit.keep_steel ? `keep ${a.limit.keep_steel} steel` : "", a.limit.max_units ? `up to ${a.limit.max_units}` : ""].filter(Boolean).join(", ") || "no limits";
  if ("prefer" in a) return `${(TARGET_WORDS.find(([k]) => k === a.prefer.target)?.[1] ?? a.prefer.target).toLowerCase()} first`;
  const f = FIRE_WORDS.find(([k]) => k === a.posture.fire)?.[1] ?? a.posture.fire;
  const m = MOVE_WORDS.find(([k]) => k === a.posture.moves)?.[1] ?? a.posture.moves;
  return `${f.toLowerCase()}, ${m.toLowerCase()}`;
}
export const ruleText = (r: Rule) => `${condText(r.when)} → ${r.then.map(actionText).join(", ")}`;

/** The server's error, readable: `OverBudget { cost: 250, budget: 200 }`,
 *  `Parse("...")`, `Invalid("...")`. */
export function errorText(e: string): string {
  const over = /OverBudget \{ cost: (\d+), budget: (\d+) \}/.exec(e);
  if (over) return `Over budget: it costs ${over[1]} of ${over[2]}. Smaller radii, or checking less often, cost less.`;
  const m = /^(?:Parse|Invalid)\("(.*)"\)$/s.exec(e);
  const s = m ? m[1].replace(/\\"/g, '"').replace(/\\n/g, " ").replace(/\\\\/g, "\\") : e;
  return s;
}

// ---- YAML out ----

const PLAIN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const RESERVED = /^(true|false|null|yes|no|on|off|y|n|~)$/i;
function scalar(v: unknown): string {
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  const s = String(v);
  return PLAIN.test(s) && !RESERVED.test(s) ? s : JSON.stringify(s);
}
function emit(v: unknown, indent: number): string[] {
  const pad = " ".repeat(indent);
  if (Array.isArray(v)) {
    if (!v.length) return [`${pad}[]`];
    return v.flatMap((item) => {
      const lines = emit(item, indent + 2);
      lines[0] = `${pad}- ${lines[0].slice(indent + 2)}`;
      return lines;
    });
  }
  if (v && typeof v === "object") {
    return Object.entries(v as Record<string, unknown>).flatMap(([k, x]) => {
      if (x && typeof x === "object" && (!Array.isArray(x) || x.length)) return [`${pad}${k}:`, ...emit(x, indent + 2)];
      return [`${pad}${k}: ${Array.isArray(x) ? "[]" : scalar(x)}`];
    });
  }
  return [`${pad}${scalar(v)}`];
}
export function toYaml(p: Policy): string {
  return emit({ name: p.name, every: p.every, rules: p.rules.map((r) => ({ priority: r.priority, when: r.when, then: r.then })) }, 0).join("\n") + "\n";
}

// ---- YAML in (block style, as the server writes it) ----

function unquote(s: string): unknown {
  if (s === "[]") return [];
  if (s === "{}") return {};
  if (s.startsWith('"')) return JSON.parse(s);
  if (s.startsWith("'")) return s.slice(1, -1).replace(/''/g, "'");
  if (/^-?\d+$/.test(s)) return Number(s);
  if (s === "true" || s === "false") return s === "true";
  if (s === "null" || s === "~") return null;
  return s;
}
/** Reads block YAML (maps, "- " sequences, plain or quoted scalars): the
 *  shape serde_yaml writes. Throws on anything else. */
export function fromYaml(text: string): unknown {
  const lines = text
    .split("\n")
    .map((l) => l.replace(/\r$/, ""))
    .filter((l) => l.trim() && !l.trim().startsWith("#") && l.trim() !== "---");
  let i = 0;
  const ind = (l: string) => l.length - l.trimStart().length;
  const node = (indent: number): unknown => {
    const first = lines[i];
    if (first.slice(indent).startsWith("- ") || first.slice(indent) === "-") {
      const out: unknown[] = [];
      while (i < lines.length && ind(lines[i]) === indent && lines[i].slice(indent).startsWith("-")) {
        const rest = lines[i].slice(indent + 1).trimStart();
        if (!rest) {
          i++;
          out.push(node(ind(lines[i])));
        } else if (/^[^"'\s][^:]*:( |$)/.test(rest)) {
          // A map begun on the dash's line: read it as if indented.
          lines[i] = " ".repeat(indent + 2) + rest;
          out.push(node(indent + 2));
        } else {
          out.push(unquote(rest));
          i++;
        }
      }
      return out;
    }
    if (!/^[^"'\s][^:]*:( |$)/.test(first.slice(indent))) {
      i++;
      return unquote(first.trim());
    }
    const out: Record<string, unknown> = {};
    while (i < lines.length && ind(lines[i]) === indent && !lines[i].slice(indent).startsWith("-")) {
      const m = /^([^:]+):\s*(.*)$/.exec(lines[i].slice(indent));
      if (!m) throw new Error(`can't read: ${lines[i].trim()}`);
      i++;
      if (m[2]) out[m[1]] = unquote(m[2]);
      else if (i < lines.length && (ind(lines[i]) > indent || (ind(lines[i]) === indent && lines[i].slice(indent).startsWith("-")))) out[m[1]] = node(ind(lines[i]));
      else out[m[1]] = null;
    }
    return out;
  };
  if (!lines.length) throw new Error("empty");
  return node(ind(lines[0]));
}
export function policyFromYaml(text: string): Policy {
  const p = fromYaml(text) as Policy;
  if (!p || typeof p !== "object" || !Array.isArray(p.rules)) throw new Error("not a policy");
  return { name: String(p.name ?? ""), every: Number(p.every ?? 300), rules: p.rules.map((r) => ({ priority: Number(r.priority ?? 0), when: r.when, then: r.then ?? [] })) };
}

// ---- Presets ----

export const PRESETS: [label: string, Policy][] = [
  [
    "Fall back when it goes badly",
    {
      name: "Fall back when it goes badly",
      every: 300,
      rules: [{ priority: 10, when: { any: [{ strength_below: { pct: 50 } }, { ammo_below: { pct: 20 } }] }, then: ["withdraw"] }],
    },
  ],
  [
    "Fall back when hurt",
    {
      name: "Fall back when hurt",
      every: 300,
      rules: [{ priority: 10, when: { health_below: { pct: 50 } }, then: ["withdraw"] }],
    },
  ],
  [
    "Go to ground under fire",
    {
      name: "Go to ground under fire",
      every: 300,
      rules: [
        { priority: 10, when: { under_fire: { pct: 30 } }, then: ["halt", { posture: { fire: "Return", moves: "Hold" } }] },
        { priority: 0, when: "always", then: ["resume"] },
      ],
    },
  ],
  [
    "Dig in on arrival",
    {
      name: "Dig in on arrival",
      every: 300,
      rules: [{ priority: 10, when: { arrived: { pct: 80 } }, then: [{ posture: { fire: "Return", moves: "Hold" } }] }],
    },
  ],
  [
    "Defend: engage what comes within 30, then resume",
    {
      name: "Defend",
      every: 300,
      rules: [
        { priority: 10, when: { enemies_within: { radius: 30, at_least: 1 } }, then: [{ engage: { radius: 30 } }] },
        { priority: 0, when: "always", then: ["resume"] },
      ],
    },
  ],
  [
    "Hold and return fire",
    {
      name: "Hold and return fire",
      every: 300,
      rules: [{ priority: 0, when: "always", then: [{ posture: { fire: "Return", moves: "Hold" } }] }],
    },
  ],
];
export const blank = (): Policy => ({ name: "New policy", every: 300, rules: [{ priority: 0, when: newLeaf("enemies_within"), then: [newAction("halt")] }] });

/** A barracks' presets, from its designs (ids differ per world). */
export function barracksPresets(designs: { id: number; name: string }[]): [string, Policy][] {
  const find = (re: RegExp) => designs.find((d) => re.test(d.name))?.id;
  const at = find(/anti-?tank/i);
  const rifle = find(/rifle/i) ?? designs[0]?.id;
  const out: [string, Policy][] = [];
  if (at !== undefined && rifle !== undefined)
    out.push([
      "Anti-tank when armour comes",
      {
        name: "Anti-tank when armour comes",
        every: 300,
        rules: [
          { priority: 10, when: { enemies_within: { radius: 12, at_least: 1 } }, then: [{ produce: { design: at } }] },
          { priority: 0, when: "always", then: [{ produce: { design: rifle } }] },
        ],
      },
    ]);
  out.push([
    "Save steel while it's quiet",
    {
      name: "Save steel while it's quiet",
      every: 300,
      rules: [
        { priority: 10, when: { enemies_within: { radius: 20, at_least: 1 } }, then: [{ limit: { keep_steel: 0, max_units: 0 } }] },
        { priority: 5, when: { stock_below: { good: STEEL, amount: 2000 } }, then: [{ limit: { keep_steel: 1500, max_units: 0 } }] },
        { priority: 0, when: "always", then: [{ limit: { keep_steel: 0, max_units: 0 } }] },
      ],
    },
  ]);
  return out;
}
export const blankBarracks = (): Policy => ({ name: "New barracks policy", every: 300, rules: [{ priority: 0, when: "always", then: [newAction("produce")] }] });
